import React, { useState, useMemo } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { setDoc, serverTimestamp, doc } from "firebase/firestore";
import { db } from '../context/firebase';
import { UserAuth } from '../context/context';

import Select from 'react-select';
import countryList from 'react-select-country-list';
import { useFormik } from 'formik';
import * as Yup from "yup";
import "./signin.css";
import logo from "../assets/logo.png";
import { PiPasswordBold } from "react-icons/pi";
import { IoMailOutline } from "react-icons/io5";
import { FiUserCheck } from "react-icons/fi";

const SignUp = () => {
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(""); // State for backend error feedback
  const { createUser } = UserAuth();
  const navigate = useNavigate();

  const options = useMemo(() => countryList().getData(), []);

  const formik = useFormik({
    initialValues: {
      email: "",
      firstname: "",
      lastname: "",
      password: "",
      country: "",
      repassword: "",
      checked: false
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email is required")
        .email("Invalid email format"),
      password: Yup.string()
        .required("Password is required")
        .min(6, 'Password must be at least 6 characters'),
      repassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords do not match')
        .required('Please confirm password'),
      firstname: Yup.string().required("First Name is required").min(2, 'Enter a valid name'),
      lastname: Yup.string().required("Last name is required").min(2, 'Enter a valid name'),
      country: Yup.string().required("Country is required"),
      checked: Yup.boolean().oneOf([true], 'You must accept the terms'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setAuthError(""); // Reset error state

      try {
        // 1. Create user in Firebase Authentication
        const userCredential = await createUser(values.email, values.password);
        const user = userCredential.user;

        if (user) {
          // 2. Save additional data to Firestore
          await setDoc(doc(db, 'users', user.uid), {
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
            createdAt: serverTimestamp(),
            country: values.country,
            userID: user.uid
          }, { merge: true });

          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Full Error Object:', error);
        
        // 3. Map Firebase Error Codes to User-Friendly Messages
        switch (error.code) {
          case 'auth/email-already-in-use':
            setAuthError("This email is already registered. Please login instead.");
            break;
          case 'auth/invalid-email':
            setAuthError("The email address is not valid.");
            break;
          case 'auth/operation-not-allowed':
            setAuthError("Email/password accounts are not enabled. Contact support.");
            break;
          case 'auth/weak-password':
            setAuthError("The password is too weak.");
            break;
          case 'auth/network-request-failed':
            setAuthError("Network error. Please check your connection.");
            break;
          default:
            setAuthError("Failed to create account. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const changeHandler = selectedOption => {
    formik.setFieldValue('country', selectedOption.label);
  };

  return (
    <div className="signin">
      <div className="signlogo">
        <img className="h-62 w-226 imagelogo" src={logo} alt="ascentinvestments" />
        
        <div className="signform w-full">
          <h1 className="nunito form-head">Create an Account</h1>

          {/* Global Auth Error Feedback */}
          {authError && (
            <div className="auth-error-box nunito" style={{ color: 'red', marginBottom: '15px', textAlign: 'center', fontSize: '14px' }}>
              {authError}
            </div>
          )}

          <form style={{justifyContent:'center', flex: '1'}} onSubmit={formik.handleSubmit}>
            {/* First Name */}
            <div className="pado">
              <p className="nunito input-header">First Name <span style={{ color: "red" }}>*</span></p>
              <span className='errors nunito'>{formik.touched.firstname && formik.errors.firstname}</span>
              <div className={`input-with-icon ${formik.touched.firstname && formik.errors.firstname ? 'input-error' : ''}`}>
                <input type="text" placeholder="Enter first name" {...formik.getFieldProps('firstname')} />
                <FiUserCheck color="#1a81c5" className="input-icon" />
              </div>
            </div>

            {/* Last Name */}
            <div className="pado">
              <p className="nunito input-header">Last Name <span style={{ color: "red" }}>*</span></p>
              <span className='errors nunito'>{formik.touched.lastname && formik.errors.lastname}</span>
              <div className={`input-with-icon ${formik.touched.lastname && formik.errors.lastname ? 'input-error' : ''}`}>
                <input type="text" placeholder="Enter last name" {...formik.getFieldProps('lastname')} />
                <FiUserCheck color="#1a81c5" className="input-icon" />
              </div>
            </div>

            {/* Email */}
            <div className="pado">
              <p className="nunito input-header">Your Email <span style={{ color: "red" }}>*</span></p>
              <span className='errors nunito'>{formik.touched.email && formik.errors.email}</span>
              <div className={`input-with-icon ${formik.touched.email && formik.errors.email ? 'input-error' : ''}`}>
                <input style={{paddingLeft: '40px'}}type="email" placeholder="mail@example.com" {...formik.getFieldProps('email')} />
                <IoMailOutline color="#1a81c5" className="input-icon" />
              </div>
            </div>

            {/* Country Select */}
            <div className="pado">
              <p className="nunito input-header">Country <span style={{ color: "red" }}>*</span></p>
              <span className='errors nunito'>{formik.touched.country && formik.errors.country}</span>
              <Select 
                options={options} 
                placeholder="Choose Country" 
                isSearchable={true} 
                onChange={changeHandler}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: (formik.touched.country && formik.errors.country) ? 'red' : state.isFocused ? '#1a81c5' : '#dee2e6',
                    fontFamily: 'nunito',
                    fontSize: '15px'
                  })
                }}
              />
            </div>

            {/* Password */}
            <div className="pado">
              <p className="nunito input-header">Password <span style={{ color: "red" }}>*</span></p>
              <span className='errors nunito'>{formik.touched.password && formik.errors.password}</span>
              <div className={`input-with-icon ${formik.touched.password && formik.errors.password ? 'input-error' : ''}`}>
                <input type="password" placeholder="Enter password" {...formik.getFieldProps('password')} />
                <PiPasswordBold color="#1a81c5" className="input-icon" />
              </div>
            </div>

            {/* Confirm Password */}
            <div className="pado">
              <p className="nunito input-header">Confirm Password <span style={{ color: "red" }}>*</span></p>
              <span className='errors nunito'>{formik.touched.repassword && formik.errors.repassword}</span>
              <div className={`input-with-icon ${formik.touched.repassword && formik.errors.repassword ? 'input-error' : ''}`}>
                <input type="password" placeholder="Re-enter password" {...formik.getFieldProps('repassword')} />
                <PiPasswordBold color="#1a81c5" className="input-icon" />
              </div>
            </div>

            {/* Terms Checkbox */}
            <div className='flex items-center nunito pt-5'>
              <input type="checkbox" {...formik.getFieldProps('checked')} style={{ marginRight: '10px' }} />
              <p className='accept'>I Accept the Terms And Privacy Policy</p>
            </div>
<div style={{display:'flex', flexDirection:'column'}}>
            <span className='errors nunito'>{formik.touched.checked && formik.errors.checked}</span>
<button 
              type='submit' 
              className="nunito signinbut" 
              disabled={loading || !formik.isValid}
            >
              {loading ? "Registering..." : "Register"}
            </button> 
</div>
            
          </form>

          <p className="dont nunito">
            Already have an account? <NavLink to="/login"><span style={{ fontWeight: 700 }}>Login</span></NavLink>
          </p>
          <p className="copyright nunito">© Copyright 2024 Ascent Investment Ltd. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;